import { Seo } from "../components/Seo";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Sparkles, ShieldCheck, HeartHandshake } from "lucide-react";
import { Button } from "../components/ui/button";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { ReviewsMarquee } from "../components/ReviewsMarquee";
import { Handwritten } from "../components/Handwritten";
import { useBooking } from "../context/BookingContext";
import { IMG } from "../lib/media";
import api from "../lib/api";

export default function Home() {
  const { openBooking } = useBooking();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Seo description="Iridescent Medical & Wellness Spa in Clinton Township, MI — expert-led Botox, fillers, laser, RF microneedling, facials & body contouring. Book your consultation today." path="/" />
      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-28 pb-16 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-12 items-center w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5C6664] mb-6">
              <span className="h-1.5 w-1.5 rounded-full iri-gradient" /> Clinton Township · Michigan
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tighter text-ink leading-[1.05]">
              Radiance,<br />
              <Handwritten
                words={["redefined", "reimagined", "reenvisioned"]}
                className="font-accent italic iri-text"
              />
            </h1>
            <p className="mt-7 text-base md:text-lg leading-relaxed text-[#5C6664] font-light max-w-md">
              Expert-led medical aesthetics — injectables, facials, RF microneedling, body
              contouring and hair restoration — thoughtfully tailored to your skin.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Button
                data-testid="hero-book-now-button"
                onClick={() => openBooking()}
                className="iri-gradient text-ink font-medium rounded-full h-12 px-8 text-base hover:scale-105 transition-transform"
              >
                Book Now
              </Button>
              <Link to="/services">
                <Button
                  variant="outline"
                  data-testid="hero-explore-button"
                  className="rounded-full h-12 px-8 text-base border-ink/20 text-ink hover:bg-[rgba(224,195,252,0.12)]"
                >
                  Explore treatments <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
            <Link
              to="/quiz"
              data-testid="hero-quiz-link"
              className="mt-5 inline-flex items-center gap-2 text-sm text-[#5C6664] hover:text-ink transition-colors group"
            >
              <Sparkles className="h-4 w-4 text-iri-mid" />
              Not sure where to start? Take our 60-second treatment finder
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
            <div className="mt-10 flex items-center gap-x-4 gap-y-3 flex-wrap md:flex-nowrap md:whitespace-nowrap" data-testid="hero-trust-strip">
              <div className="flex flex-col">
                <div className="flex items-center gap-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-3.5 w-3.5 fill-iri-mid text-iri-mid" />
                  ))}
                </div>
                <span className="text-xs text-ink mt-1">Loved by 3,000+ Michigan Clients</span>
              </div>
              <span className="hidden md:block h-8 w-px bg-black/10 shrink-0" />
              <div className="flex flex-col">
                <p className="font-heading text-base font-light text-ink leading-none">12,500+</p>
                <span className="text-[11px] text-[#8A9492] mt-1">Treatments since 2024</span>
              </div>
              <span className="hidden md:block h-8 w-px bg-black/10 shrink-0" />
              <div className="flex flex-col">
                <p className="font-heading text-base font-light text-ink leading-none">Over 15</p>
                <span className="text-[11px] text-[#8A9492] mt-1">Top Aesthetic Devices</span>
              </div>
            </div>
          </motion.div>

          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="img-zoom rounded-[2rem] overflow-hidden aspect-[4/5] shadow-2xl shadow-blue-900/10">
              <img src={IMG.hero} alt="Iridescent Wellness Spa interior" className="w-full h-full object-cover" />
            </div>
            <motion.div
              className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl shadow-blue-900/10 w-48"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.7 }}
            >
              <p className="text-3xl font-heading font-light text-ink">15+</p>
              <p className="text-xs text-[#5C6664] mt-1">Advanced Treatments Performed By Licensed Experts</p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* FIRST VISIT OFFER */}
      <section className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <button
              onClick={() => openBooking("First Time Client Lift & Glow + Consult")}
              data-testid="home-first-visit-cta"
              className="group w-full text-left block rounded-[1.75rem] overflow-hidden relative bg-ink text-white"
            >
              <img src={IMG.facial} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
              <div className="relative px-6 py-8 md:px-12 md:py-10 flex flex-col md:flex-row md:items-center gap-5 md:gap-10">
                <div className="flex-1">
                  <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] iri-text font-medium">
                    <Sparkles className="h-3.5 w-3.5" /> New client offer
                  </span>
                  <h2 className="font-heading text-2xl md:text-3xl font-light mt-2 leading-snug">
                    First Time Visit — Lift &amp; Glow Facial
                    <span className="text-white/70"> + Complimentary Consultation</span>
                  </h2>
                  <p className="text-white/60 text-sm mt-2">A perfect introduction to Iridescent — book online in under a minute.</p>
                </div>
                <div className="flex items-center gap-5 shrink-0">
                  <p className="font-heading text-4xl font-light">$99<span className="text-xl">.95</span></p>
                  <span className="inline-flex items-center gap-2 iri-gradient text-ink font-medium rounded-full h-12 px-7 group-hover:scale-105 transition-transform">
                    Reserve now <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </div>
            </button>
          </Reveal>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-black/5 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 grid grid-cols-2 md:grid-cols-3 gap-8">
          {[
            [ShieldCheck, "Expert-led", "Every treatment is expert-supervised"],
            [Sparkles, "Natural results", "A 'less is more' aesthetic philosophy"],
            [HeartHandshake, "Tailored care", "Personalized plans for your goals"],
          ].map(([Icon, title, sub], i) => (
            <Reveal key={title} delay={i * 0.1} className="flex items-start gap-4">
              <Icon className="h-6 w-6 text-iri-mid shrink-0" strokeWidth={1.5} />
              <div>
                <p className="font-heading text-ink font-medium">{title}</p>
                <p className="text-sm text-[#5C6664] mt-0.5">{sub}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES BENTO */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="max-w-2xl mb-14">
            <span className="text-xs uppercase tracking-[0.2em] text-[#5C6664]">Our treatments</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-ink">
              A curated menu of aesthetics &amp; wellness
            </h2>
          </Reveal>

          <Stagger className="grid md:grid-cols-3 gap-5 md:gap-6 auto-rows-[280px]">
            {categories.map((c, i) => (
              <StaggerItem
                key={c.slug}
                className={i === 0 ? "md:col-span-2 md:row-span-2" : ""}
              >
                <Link
                  to={`/services?category=${c.slug}`}
                  data-testid={`home-category-${c.slug}`}
                  className="group block h-full img-zoom rounded-2xl overflow-hidden relative hover-lift"
                >
                  <img src={c.image} alt={c.name} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/10 to-transparent" />
                  <div className="absolute bottom-0 p-6">
                    <h3 className="font-heading text-white text-xl md:text-2xl font-medium">{c.name}</h3>
                    <p className="text-white/70 text-sm mt-1 max-w-xs">{c.description}</p>
                    <span className="inline-flex items-center gap-1.5 text-white text-sm mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                      View treatments <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* MEMBERSHIP CTA */}
      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal className="relative rounded-[2rem] overflow-hidden">
            <img src={IMG.texture} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="relative iri-gradient/0 px-8 py-16 md:px-20 md:py-24 text-center backdrop-blur-[1px]">
              <div className="bg-white/30 backdrop-blur-sm inline-block px-8 py-12 md:px-16 md:py-14 rounded-3xl">
                <span className="text-xs uppercase tracking-[0.2em] text-ink/70">VIP Membership</span>
                <h2 className="mt-4 font-heading text-3xl md:text-5xl font-light tracking-tight text-ink max-w-2xl mx-auto">
                  Glow all year, for less
                </h2>
                <p className="mt-5 text-ink/70 max-w-lg mx-auto">
                  Join free to earn points every visit — or unlock VIP facials, exclusive pricing and member perks from $239/month.
                </p>
                <Link to="/membership">
                  <Button
                    data-testid="home-membership-cta"
                    className="mt-8 bg-ink text-white rounded-full h-12 px-8 hover:bg-ink/90"
                  >
                    Discover memberships <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-24 bg-white border-y border-black/5" data-testid="reviews-section">
        <div className="max-w-3xl mx-auto px-6 text-center mb-12">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] text-iri-mid mb-3">Loved by our members</p>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-ink">
              Results that speak for themselves
            </h2>
          </Reveal>
        </div>
        <Reveal>
          <ReviewsMarquee />
        </Reveal>
      </section>
    </div>
  );
}
